"use client";

import Image from "next/image";
import Link from "next/link";
import Button from "../../ui/Button";
import UserIcon from "../../icons/UserIcon";
import CalendarIcon from "../../icons/CalendarIcon";
import FlameOutlineIcon from "../../icons/FlameOutlineIcon";
import GiftsIcon from "../../icons/GiftsIcon";
import FlashIcon from "../../icons/FlashIcon";
import TrophyIcon from "../../icons/TrophyIcon";
import CheckIcon from "../../icons/CheckIcon";
import XCircleIcon from "../../icons/XCircleIcon";

type ComparisonRow = {
  label: string;
  icon: React.ReactNode;
  dankClub: string;
  typical: string;
};

const ROWS: ComparisonRow[] = [
  {
    label: "Join today",
    icon: <UserIcon size={16} className="h-4 w-4 shrink-0" />,
    dankClub: "20% off",
    typical: "No reward",
  },
  {
    label: "6 visits",
    icon: <CalendarIcon size={16} className="h-4 w-4 shrink-0" />,
    dankClub: "10% off",
    typical: "No reward",
  },
  {
    label: "10 visits",
    icon: <FlameOutlineIcon size={16} className="h-4 w-4 shrink-0" />,
    dankClub: "20% off",
    typical: "No reward",
  },
  {
    label: "20 visits",
    icon: <GiftsIcon size={16} className="h-4 w-4 shrink-0" />,
    dankClub: "Two 20% off rewards",
    typical: "One 5% off reward",
  },
  {
    label: "38 visits",
    icon: <FlashIcon size={16} className="h-4 w-4 shrink-0" />,
    dankClub: "Three 20% off rewards + 10% off reward",
    typical: "One 10% off reward",
  },
  {
    label: "55 visits",
    icon: <TrophyIcon size={16} className="h-4 w-4 shrink-0" />,
    dankClub: "Five 20% off rewards",
    typical: "One 15% off reward",
  },
];

function CheckBadge() {
  return (
    <span
      className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-constant-white/70"
      aria-hidden
    >
      <CheckIcon size={9} color="currentColor" className="h-2 w-2" />
    </span>
  );
}

export default function DankClubComparisonSection() {
  return (
    <section className="w-full max-w-none" aria-label="Dank Club rewards compared to typical loyalty programs">
      <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-2 lg:gap-12">
        {/* Left: heading + copy + CTA */}
        <div>
          <h2 className="text-h3-mobile sm:text-h3-desktop font-bold leading-tight text-text-primary">
            Rewards that actually reward you
          </h2>
          <p className="mt-3 text-body-16-desktop leading-relaxed text-text-secondary">
            Why settle for standard points when you can get real discounts? The Dank Club is designed
            to give you bigger, faster rewards than typical dispensary programs. Join today and start
            saving on your favourite cannabis products immediately.
          </p>
          <div className="mt-8">
            <Button asChild variant="primary" size="lg" className="w-full rounded-xl sm:w-auto">
              <Link href="/register">Join Dank Club now</Link>
            </Button>
          </div>
        </div>

        {/* Right: comparison table. CSS grid keeps the three columns row-aligned;
            the middle (Dank Club) column carries its own navy card treatment. */}
        <div className="grid grid-cols-[minmax(0,0.95fr)_minmax(0,1.2fr)_minmax(0,1fr)]">
          {/* Header row */}
          <div aria-hidden />
          <div className="flex items-center justify-center rounded-t-2xl bg-blue-80 px-4 pb-3 pt-4">
            <Image
              src="/media/brand/footer-logo.webp"
              alt="Dank Club"
              width={96}
              height={49}
              className="h-9 w-auto object-contain"
            />
          </div>
          <div className="flex items-center justify-center px-3 pb-3 pt-4 text-center text-body-14-mobile font-semibold leading-snug text-text-secondary sm:text-body-14-desktop">
            Typical Loyalty Programs
          </div>

          {ROWS.map((row, i) => {
            const isLast = i === ROWS.length - 1;
            return (
              <div key={row.label} className="contents">
                {/* Milestone label */}
                <div
                  className={`flex items-center gap-2 border-t border-neutral-10 py-3 pl-3 pr-2 text-[11px] font-semibold uppercase tracking-wide text-text-primary sm:text-xs ${
                    isLast ? "border-b" : ""
                  }`}
                >
                  <span className="text-text-primary" aria-hidden>
                    {row.icon}
                  </span>
                  {row.label}
                </div>

                {/* Dank Club (highlighted) */}
                <div
                  className={`flex items-center gap-2 border-t border-constant-white/10 bg-blue-80 px-4 py-3 text-[11px] font-semibold uppercase leading-snug text-constant-white sm:text-xs ${
                    isLast ? "rounded-b-2xl pb-4" : ""
                  }`}
                >
                  <CheckBadge />
                  {row.dankClub}
                </div>

                {/* Typical programs */}
                <div
                  className={`flex items-center gap-2 border-t border-neutral-10 px-3 py-3 text-[11px] font-medium uppercase leading-snug text-text-tertiary sm:text-xs ${
                    isLast ? "border-b" : ""
                  }`}
                >
                  <XCircleIcon size={16} color="currentColor" className="h-4 w-4 shrink-0 opacity-60" />
                  {row.typical}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
